import * as fb from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { BasicResponse, DataResponse } from "functions/src/responseTypes";
import {
  BinaryTreeNodeUser,
  SearchUserResponse,
} from "functions/src/Users/UsersInterfaces";
import { ActionContext } from "vuex";
import {
  ADD_NODES,
  SET_ROOT,
  CLEAR,
  EXPAND_MANY,
  COLLAPSE_MANY,
  VIEW_STATE_DOC,
  VIEW_STATE_SCHEMA_VERSION,
  USE_NEW_TREE,
  ApplyViewStatePayload,
  BinaryTreeViewStatus,
  ExpandSubtreeRequest,
  LoadSubtreeRequest,
  RefreshNodePointsRequest,
  SaveViewStateRequest,
  BinaryTreeState,
} from "./BinaryTreeInterfaces";

// Isolated store for the new binary-tree (org-chart) view. Does NOT share state
// with the legacy `userNetwork` store used by the old binary-network view (so
// neither pollutes the other). Reuses Users-getBinaryTree for bounded fetches.

const state: BinaryTreeState = {
  rootUsername: "",
  homeUsername: "",
  nodes: new Map<string, BinaryTreeNodeUser>(),
  pointsFetchedAt: new Map<string, number>(),
  expanded: {},
  focusUsernames: [],
  revision: 0,
};

// Default staleness window for the lazy points refresh: 5 minutes. On popover
// open, a node whose points were fetched more recently than this is served from
// cache; older than this triggers a single-node re-fetch.
const DEFAULT_POINTS_TTL_MS = 5 * 60 * 1000;

// Usernames (lowercase) whose single-node points refresh is currently in flight.
// Module-scoped rather than in reactive state because it's transient control
// data, not view state; it dedupes a desktop hover and a mobile tap (or two
// overlapping debounced calls) that would otherwise fetch the same node twice.
const inFlightPointRefreshes = new Set<string>();

// childLeft/childRight come back from the callable as username strings, but the
// type also allows nested nodes — normalize both to a username.
function childUsernameOf(
  child: BinaryTreeNodeUser | string | null | undefined
): string {
  if (child === null || child === undefined) return "";
  return typeof child === "string" ? child : child.username || "";
}

// Order open usernames breadth-first from the current anchor so that, if the
// restore cap trims the list, it keeps the nodes nearest the viewport rather
// than an arbitrary slice. Open nodes outside the anchor's loaded subtree are
// appended at the end.
function orderOpenNodesByProximity(state: BinaryTreeState): string[] {
  const order = new Array<string>();
  const seen = new Set<string>();
  const queue: string[] = [state.rootUsername.toLowerCase()];
  while (queue.length > 0) {
    const u = queue.shift();
    if (u === undefined) break;
    const key = u.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    if (state.expanded[key] === true) order.push(key);
    const node = state.nodes.get(key);
    if (!node) continue;
    [node.childLeft, node.childRight].forEach((c) => {
      const cu = childUsernameOf(c);
      if (cu) queue.push(cu);
    });
  }
  Object.keys(state.expanded).forEach((k) => {
    if (!seen.has(k)) order.push(k);
  });
  return order;
}

// The ancestor column from each focus node up to the anchor. Stored so a capped
// restore can always keep this column (it's linear = depth, cheap) and land the
// user back exactly where they were, even deep in the tree.
function focusPathToAnchor(
  state: BinaryTreeState,
  focusUsernames: string[]
): string[] {
  const anchor = state.rootUsername.toLowerCase();
  const path = new Set<string>();
  (focusUsernames || []).forEach((f) => {
    let cur = (f || "").toLowerCase();
    let guard = 0;
    while (cur && guard < 10000) {
      guard++;
      path.add(cur);
      if (cur === anchor) break;
      const node = state.nodes.get(cur);
      const parent = node && node.parent ? node.parent.toLowerCase() : "";
      if (!parent || parent === cur) break;
      cur = parent;
    }
  });
  return Array.from(path);
}

const mutations = {
  ADD_NODES(state: BinaryTreeState, tree: BinaryTreeNodeUser[]) {
    // One stamp per commit: every node written here just had its `points`
    // (re)loaded, so record when — the lazy popover refresh reads this to decide
    // whether a node is still within TTL. Keyed identically to `nodes`.
    const now = Date.now();
    tree.forEach((n) => {
      const existing = state.nodes.get(n.username);
      if (existing) {
        existing.loadedLevels = n.loadedLevels;
        existing.childLeft = n.childLeft;
        existing.childRight = n.childRight;
        existing.points = n.points;
        existing.rank = n.rank;
        existing.nickname = n.nickname;
        existing.pictureUrl = n.pictureUrl;
        existing.enroller = n.enroller;
        existing.subscriptions = n.subscriptions;
        existing.binarySide = n.binarySide;
        existing.parent = n.parent;
      } else {
        state.nodes.set(n.username, n);
      }
      state.pointsFetchedAt.set(n.username, now);
    });
  },
  SET_ROOT(state: BinaryTreeState, username: string) {
    state.rootUsername = username;
    state.revision++;
  },
  SET_HOME(state: BinaryTreeState, username: string) {
    state.homeUsername = username;
  },
  // Reassign the record (instead of in-place add/delete) so the change always
  // triggers the `expanded` computed — in-place key delete was not reliably
  // reactive through the method-style getter, leaving collapses visually stuck.
  EXPAND_MANY(state: BinaryTreeState, usernames: string[]) {
    const next = { ...state.expanded };
    usernames.forEach((u) => {
      next[u] = true;
    });
    state.expanded = next;
    state.revision++;
  },
  COLLAPSE_MANY(state: BinaryTreeState, usernames: string[]) {
    const next = { ...state.expanded };
    usernames.forEach((u) => {
      delete next[u];
    });
    state.expanded = next;
    state.revision++;
  },
  CLEAR_EXPANDED(state: BinaryTreeState) {
    state.expanded = {};
    state.revision++;
  },
  SET_FOCUS(state: BinaryTreeState, usernames: string[]) {
    state.focusUsernames = Array.isArray(usernames) ? usernames : [];
  },
  APPLY_VIEW_STATE(state: BinaryTreeState, payload: ApplyViewStatePayload) {
    state.homeUsername = payload.homeUsername || "";
    state.rootUsername = payload.anchorUsername || "";
    const rec: Record<string, boolean> = {};
    (payload.openNodes || []).forEach((u) => {
      if (u) rec[u.toLowerCase()] = true;
    });
    state.expanded = rec;
    state.focusUsernames = Array.isArray(payload.focusUsernames)
      ? payload.focusUsernames
      : [];
    state.revision++;
  },
  CLEAR(state: BinaryTreeState) {
    state.rootUsername = "";
    state.homeUsername = "";
    state.nodes = new Map<string, BinaryTreeNodeUser>();
    state.pointsFetchedAt = new Map<string, number>();
    state.expanded = {};
    state.focusUsernames = [];
    state.revision = 0;
  },
};

const actions = {
  async loadSubtree(
    context: ActionContext<BinaryTreeState, any>,
    request: LoadSubtreeRequest
  ) {
    const getBinaryTree = fb.func.httpsCallable("Users-getBinaryTree");
    const r = await getBinaryTree({
      username: request.username,
      levels: request.levels,
      useNewTree: USE_NEW_TREE,
    });
    const response: DataResponse<BinaryTreeNodeUser[]> | BasicResponse = r.data;
    if (response.OPCODE === "SUCCESS" || response.OPCODE === "HAS-ERRORS") {
      context.commit(
        ADD_NODES,
        (response as DataResponse<BinaryTreeNodeUser[]>).data
      );
    } else if (response.OPCODE === "ERROR") {
      throw new Error(response.message || "No se pudo cargar la información.");
    }
  },
  // Load many nodes (and `levels` below each) in ONE call via getBinaryTreeBatch.
  // Used to prefetch the focus column on a deep cold restore so we don't cascade
  // hundreds of sequential single-node fetches (this is text only — profile
  // images stay lazy via the viewport observer).
  async loadNodesBatch(
    context: ActionContext<BinaryTreeState, any>,
    request: { usernames: string[]; levels: number }
  ): Promise<void> {
    const usernames = (request.usernames || []).filter(Boolean);
    if (usernames.length === 0) return;
    const getBatch = fb.func.httpsCallable("Users-getBinaryTreeBatch");
    const r = await getBatch({
      usernames,
      levels: request.levels,
      useNewTree: USE_NEW_TREE,
    });
    const response: DataResponse<BinaryTreeNodeUser[]> | BasicResponse = r.data;
    if (response.OPCODE === "SUCCESS" || response.OPCODE === "HAS-ERRORS") {
      context.commit(
        ADD_NODES,
        (response as DataResponse<BinaryTreeNodeUser[]>).data
      );
    } else if (response.OPCODE === "ERROR") {
      throw new Error(response.message || "No se pudo cargar la información.");
    }
  },
  // Lazy, cache-aware refresh of a SINGLE node's own points, dispatched when the
  // cursor rests on a card or se toca su botón de info (ambos entran aquí tras un
  // debounce en la vista). Serves from cache while the node's points are still
  // within `ttlMs`, so rapid hovers cost nothing; only a rested node past its
  // TTL hits the network. Fetches ONLY this node (levels: 1) — the cheapest call
  // that still returns the node's own binaryPoints summaries — and commits it
  // via ADD_NODES, which reassigns `points` reactively (la tarjeta del nodo lee
  // node.points.*.pending, así que el valor se actualiza a la vista) y re-stamps
  // pointsFetchedAt. Guarded so a hover and a tap can't double-fetch.
  async refreshNodePointsIfStale(
    context: ActionContext<BinaryTreeState, any>,
    payload: RefreshNodePointsRequest | string
  ): Promise<void> {
    const username = typeof payload === "string" ? payload : payload?.username;
    const ttlMs =
      typeof payload === "string"
        ? DEFAULT_POINTS_TTL_MS
        : payload?.ttlMs ?? DEFAULT_POINTS_TTL_MS;
    const u = (username || "").trim().toLowerCase();
    if (!u) return;

    // Fresh enough? A cached node whose points were fetched within the TTL needs
    // no network. A node missing from the cache always fetches (stamp = 0).
    const cached = context.state.nodes.get(u);
    const fetchedAt = context.state.pointsFetchedAt.get(u) ?? 0;
    if (cached && Date.now() - fetchedAt <= ttlMs) return;

    // Collapse a simultaneous hover+tap (or overlapping debounced calls) for the
    // same node into a single request.
    if (inFlightPointRefreshes.has(u)) return;
    inFlightPointRefreshes.add(u);

    // Preserve the deeper-load marker. This node may already have descendants
    // loaded (loadedLevels > 1); a levels:1 refresh would otherwise report
    // loadedLevels = 1 and make the next expand click needlessly re-fetch a
    // subtree it already holds. Keeping the max leaves expand/collapse untouched.
    const prevLoadedLevels = cached?.loadedLevels ?? 0;
    try {
      const getBatch = fb.func.httpsCallable("Users-getBinaryTreeBatch");
      const r = await getBatch({
        usernames: [u],
        levels: 1,
        useNewTree: USE_NEW_TREE,
      });
      const response: DataResponse<BinaryTreeNodeUser[]> | BasicResponse =
        r.data;
      if (response.OPCODE === "SUCCESS" || response.OPCODE === "HAS-ERRORS") {
        const nodes = (response as DataResponse<BinaryTreeNodeUser[]>).data;
        nodes.forEach((n) => {
          if ((n.loadedLevels || 0) < prevLoadedLevels) {
            n.loadedLevels = prevLoadedLevels;
          }
        });
        context.commit(ADD_NODES, nodes);
      }
      // A non-SUCCESS OPCODE (ERROR / NOT_PLACED / …) is intentionally silent:
      // this is a background refresh, so the popover simply keeps its cached
      // value rather than surfacing an error for a mere hover.
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("binaryTree: no se pudo refrescar los puntos de", u, error);
    } finally {
      inFlightPointRefreshes.delete(u);
    }
  },
  async setRoot(
    context: ActionContext<BinaryTreeState, any>,
    username: string
  ): Promise<string> {
    let u = (username || "").trim().toLowerCase();
    if (u.includes("@")) {
      const searchUser = fb.func.httpsCallable("Users-searchUser");
      const r = await searchUser({ username: u });
      const response: DataResponse<SearchUserResponse> | BasicResponse = r.data;
      if (response.OPCODE === "SUCCESS") {
        u = (response as DataResponse<SearchUserResponse>).data.usernamePK;
      } else if (response.OPCODE === "ERROR") {
        throw new Error(
          response.message || "No se pudo cargar la información."
        );
      }
    }
    context.commit(SET_ROOT, u);
    return u;
  },
  // Mark `username` and its loaded descendants down to (levels - 1) below it as
  // expanded, so a click reveals exactly `levels` descendant levels. Only walks
  // nodes already in the store, so callers must load the data first.
  expandSubtree(
    context: ActionContext<BinaryTreeState, any>,
    request: ExpandSubtreeRequest
  ) {
    const toExpand = new Array<string>();
    const seen = new Set<string>();
    const queue: Array<{ u: string; d: number }> = [
      { u: request.username, d: 0 },
    ];
    while (queue.length > 0) {
      const item = queue.shift();
      if (!item) break;
      const key = item.u.toLowerCase();
      if (seen.has(key) || item.d >= request.levels) continue;
      seen.add(key);
      const node = context.getters.nodeFor(key) as
        | BinaryTreeNodeUser
        | undefined;
      if (!node) continue;
      toExpand.push(key);
      [node.childLeft, node.childRight].forEach((c) => {
        const cu = childUsernameOf(c);
        if (cu) queue.push({ u: cu, d: item.d + 1 });
      });
    }
    context.commit(EXPAND_MANY, toExpand);
  },
  // Collapse `username` and its entire loaded subtree, so re-expanding starts
  // fresh (a click always reveals exactly the configured number of levels).
  collapseSubtree(
    context: ActionContext<BinaryTreeState, any>,
    username: string
  ) {
    const toCollapse = new Array<string>();
    const seen = new Set<string>();
    const queue: string[] = [username];
    while (queue.length > 0) {
      const u = queue.shift();
      if (u === undefined) break;
      const key = u.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      toCollapse.push(key);
      const node = context.getters.nodeFor(key) as
        | BinaryTreeNodeUser
        | undefined;
      if (!node) continue;
      [node.childLeft, node.childRight].forEach((c) => {
        const cu = childUsernameOf(c);
        if (cu) queue.push(cu);
      });
    }
    context.commit(COLLAPSE_MANY, toCollapse);
  },
  resetTree(context: ActionContext<BinaryTreeState, any>) {
    context.commit(CLEAR);
  },
  // Read the persisted view state for `username` (the logged-in user) from
  // users/{username}/userSettings/binaryTreeViewStatus. Returns null if absent.
  async loadViewState(
    context: ActionContext<BinaryTreeState, any>,
    username: string
  ): Promise<BinaryTreeViewStatus | null> {
    const u = (username || "").trim();
    if (!u) return null;
    try {
      const snap = await getDoc(
        doc(fb.firestore, "users", u, "userSettings", VIEW_STATE_DOC)
      );
      return snap.exists() ? (snap.data() as BinaryTreeViewStatus) : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("binaryTree: no se pudo leer el estado guardado", error);
      return null;
    }
  },
  // Persist the current view structure (anchor, home, open nodes, settings) for
  // the logged-in user. Volatile node data is intentionally NOT stored.
  async saveViewState(
    context: ActionContext<BinaryTreeState, any>,
    request: SaveViewStateRequest
  ): Promise<void> {
    const u = (request.username || "").trim();
    if (!u || !context.state.rootUsername) return;
    try {
      const payload: BinaryTreeViewStatus = {
        _usernamePK: u.toLowerCase(),
        homeUsername: context.state.homeUsername,
        anchorUsername: context.state.rootUsername,
        openNodes: orderOpenNodesByProximity(context.state),
        expandDepth: request.expandDepth,
        focusUsernames: request.focusUsernames || [],
        focusPath: focusPathToAnchor(context.state, request.focusUsernames),
        savedAt: Date.now(),
        schemaVersion: VIEW_STATE_SCHEMA_VERSION,
      };
      await setDoc(
        doc(fb.firestore, "users", u, "userSettings", VIEW_STATE_DOC),
        payload,
        { merge: true }
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("binaryTree: no se pudo guardar el estado", error);
    }
  },
};

const getters = {
  nodeFor(state: BinaryTreeState) {
    return (username: string): BinaryTreeNodeUser | undefined => {
      if (!username) return undefined;
      return state.nodes.get(username.toLowerCase());
    };
  },
  nodeCount(state: BinaryTreeState): number {
    return state.nodes.size;
  },
  treeRoot(state: BinaryTreeState): string {
    return state.rootUsername;
  },
  homeRoot(state: BinaryTreeState): string {
    return state.homeUsername;
  },
  isExpanded(state: BinaryTreeState) {
    return (username: string): boolean =>
      !!username && state.expanded[username.toLowerCase()] === true;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
